// Ask for an image at the size it will actually be displayed. Our own uploads are resized and
// cached server-side; Unsplash does it via its own query params. Anything else is left alone.
export const thumb = (src, w = 480) => {
  if (!src || typeof src !== "string") return src;
  try {
    const u = new URL(src, window.location.origin);
    if (u.hostname.endsWith("unsplash.com")) {
      u.searchParams.set("w", String(w));
      u.searchParams.set("q", "70");
      u.searchParams.set("auto", "format");
      return u.toString();
    }
    if (u.pathname.includes("/api/uploads/")) {
      if (u.searchParams.get("sig")) return src; // signed private document — never rewrite
      u.searchParams.set("w", String(w));
      return u.toString();
    }
  } catch {
    return src;
  }
  return src;
};

// Downscale + re-encode an image file to a web-friendly JPEG before upload.
// Fixes oversized phone photos (HTTP 413) and converts formats the browser can
// decode (e.g. HEIC on iOS Safari) into a universally displayable JPEG.
export async function compressImage(file, { maxDim = 1600, quality = 0.85, maxBytes = 7 * 1024 * 1024 } = {}) {
  if (!file || !file.type?.startsWith("image/")) return file;
  // GIFs are often animated — re-encoding would flatten them, so leave small ones alone.
  if (file.type === "image/gif" && file.size <= maxBytes) return file;

  let bitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    // Browser can't decode this format (e.g. HEIC on Android/Chrome) — send original and let the server decide.
    return file;
  }

  const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height));
  const w = Math.round(bitmap.width * scale);
  const h = Math.round(bitmap.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(bitmap, 0, 0, w, h);
  bitmap.close?.();

  const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
  if (!blob) return file;
  const name = (file.name || "photo").replace(/\.[^.]+$/, "") + ".jpg";
  return new File([blob], name, { type: "image/jpeg", lastModified: Date.now() });
}
